import { Component, DestroyRef, ViewChild, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdService } from '../../services/id/id.service';
import { ActivatedRoute, Router } from "@angular/router";
import { PermissionsService } from "../../services/permissions/permissions.service";
import Constants from "../../shared/constants";
import * as Cookies from 'js-cookie';
import { environment } from "../../../environments/environment";
import { SeoServiceService, TranslateService } from "@xref/ngx";
import { MarketingService } from "../../services/marketing/marketing.service";
import { CountryPhoneCodesService } from '../../services/country-phone-codes/country-phone-codes.service';
import { PhoneNumberUtil } from "google-libphonenumber";


@Component({
    selector: 'xlog-signup',
    templateUrl: './signup.component.pug',
    styleUrls: ['./signup.component.scss'],
    standalone: false
})
export class SignupComponent implements OnInit, OnDestroy {
    @ViewChild('inputElement', { read: ElementRef }) inputElement!: ElementRef;
    @ViewChild('areaCodeDropdown') areaCodeDropdown: any;

    routerData: any;
    page = 'welcomePage';
    loaded = false;
    model = {
        email: '',
        first_name: '',
        last_name: '',
        iso_code: 'AU',
        area_code: '+61',
        mobile: '',
        org_name: '',
        org_size: '',
        preferred_language: 'en-au'
    };
    btnDisable = false;
    didNotReceiveEmailText = '';
    resendText = '';
    sendingEmailFailed = false;
    token = '';
    screenWidth: number;
    errors: object = {
        email: { enabled: false, msg: '' },
        first_name: { enabled: false, msg: '' },
        last_name: { enabled: false, msg: '' },
        mobile: { enabled: false, msg: '' },
        org_name: { enabled: false, msg: '' }
    };
    timeoutId: any;
    areaCodes: any[] = [];
    areaCodeDisplay = '+61';
    ts_and_cs_link = Constants.SIGNUP_TERMS_XREF_URL;
    ff_ent_10781_sign_up = false;

    readonly orgSizes = Constants.ORG_SIZES;

    private _areaCodeOpening = false;
    private _bodyObserver: MutationObserver;

    constructor(
        public id: IdService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private featureFlagService: PermissionsService,
        private translateService: TranslateService,
        private marketingService: MarketingService,
        public countryCodes: CountryPhoneCodesService,
        private seoService: SeoServiceService,
        private destroyRef: DestroyRef
    ) { }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.screenWidth = window.innerWidth;
    }

    ngOnInit() {
        this.ff_ent_10781_sign_up = this.featureFlagService.isFeatureActivated('ent-10781-signup-improvements');
        this.activatedRoute.data.subscribe(data => {
            this.routerData = data;
            this.seoService.updateTitle(data.title);
            this.seoService.updateDescription(data.description);
        });
        this.page = this.routerData.page;

        this.activatedRoute.queryParams.subscribe(params => {
            if (this.page === 'manualUpgradePage') {
                this.token = params['t'];
                if (!this.token) {
                    this.router.navigate(['signup/expired']);
                }
            }
            if (params['email']) {
                this.model.email = params['email'];
                Cookies.set('user_email', params['email'], { domain: environment.DOMAIN });
            } else {
                const savedEmail = Cookies.get('user_email');
                if (savedEmail) {
                    this.model.email = savedEmail;
                }
            }

            const cookieKeys = ['plan', 'org_size', 'currency'];
            cookieKeys.forEach(key => {
                if (params[key]) {
                    Cookies.set(key, params[key], { domain: environment.DOMAIN });
                } else {
                    Cookies.remove(key, { domain: environment.DOMAIN });
                }
            });
        });

        this.marketingService.setMarketingResource("sign-up-marketing-section");
        this.id.signupOriginAuditLog(this.router.url).subscribe();
        this.onResize(null);
        this.countryCodes.countriesReady$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.populateAreaCodes());
        if (this.ff_ent_10781_sign_up && this.page === 'welcomePage') {
            this.setupAreaCodeDropdownObserver();
        }
        this.loaded = true;
        this.populateErrorMessages();
    }

    ngOnDestroy() {
        this._bodyObserver?.disconnect();
    }

    populateErrorMessages() {
        const keys = [
            'signUpText.welcomePage.errors.invalidDomain',
            'signUpText.welcomePage.errors.firstNameRequired',
            'signUpText.welcomePage.errors.lastNameRequired',
            'signUpText.welcomePage.errors.mobileInvalid',
            'signUpText.welcomePage.errors.orgNameRequired'
        ];
        this.translateService.get(keys)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(translations => {
                this.errors['email'].msg = translations[keys[0]];
                this.errors['first_name'].msg = translations[keys[1]];
                this.errors['last_name'].msg = translations[keys[2]];
                this.errors['mobile'].msg = translations[keys[3]];
                this.errors['org_name'].msg = translations[keys[4]];
            });
    }

    populateAreaCodes() {
        const mostUsed = this.countryCodes.mostUsedCountries.map(country => ({
            key: country.ext,
            ext: country.ext,
            isoCode: country.isoCode,
            value: `${country.name} (${country.ext})`
        }));
        const allCountries = this.countryCodes.countryList.map(country => ({
            key: country.ext,
            isoCode: country.isoCode,
            value: `${country.name} (${country.ext})`
        }));
        this.areaCodes = [...mostUsed, ...allCountries];
    }

    loadVerifyEmailPage() {
        this.page = 'verifyEmailPage';

        this.didNotReceiveEmailText = 'signUpText.verifyEmailPage.didNotReceiveEmailA';
        this.resendText = 'signUpText.verifyEmailPage.resendA';
    }

    sendEmailSuccess = (response: any) => {
        this.loaded = true;
        this.sendingEmailFailed = false;
        this.loadVerifyEmailPage();
        Cookies.remove('user_email', { domain: environment.DOMAIN });
    };

    sendEmailError = (response: any) => {
        this.loaded = true;
        if (response.status == 400 && response.error && response.error['email'][0] === "Invalid email domain") {
            this.sendingEmailFailed = false;
            this.errors['email'].enabled = true;
        } else {
            this.errors['email'].enabled = false;
            this.sendingEmailFailed = true;
        }
    };

    sendEmail() {
        if (this.timeoutId) {
            this.btnDisable = false;
            clearTimeout(this.timeoutId);
        }

        this.loaded = false;
        this.model.preferred_language = (Cookies.get(Constants.LOCALE_TOKEN) || Constants.LOCALE);
        this.id.generateEmailVerificationToken(this.model, this.sendEmailSuccess, this.sendEmailError);
    }

    resendEmailSuccess = (response: any) => {
        this.loaded = true;
        this.sendingEmailFailed = false;
        this.loadVerifyEmailPageAgain();
    };

    resendEmail() {
        this.loaded = false;
        this.model.preferred_language = (Cookies.get(Constants.LOCALE_TOKEN) || Constants.LOCALE);
        this.id.generateEmailVerificationToken(this.model, this.resendEmailSuccess, this.sendEmailError);
    }

    loadVerifyEmailPageAgain() {
        this.didNotReceiveEmailText = 'signUpText.verifyEmailPage.didNotReceiveEmailB';
        this.resendText = 'signUpText.verifyEmailPage.resendB';
        this.btnDisable = true;

        this.timeoutId = setTimeout(() => {
            this.didNotReceiveEmailText = 'signUpText.verifyEmailPage.didNotReceiveEmailC';
            this.resendText = 'signUpText.verifyEmailPage.resendC';
            this.btnDisable = false;
        }, 60000);
    }

    backToWelcomePage() {
        this.page = 'welcomePage';
        this.sendingEmailFailed = false;
        this.model.email = '';
    }

    routeToWelcomePage() {
        this.router.navigate(['/signup']);
    }

    contactXref() {
        window.open('https://www.xref.com/contact-us?src=xref-platform&ltyp=omni&cn=sign-up-app', '_blank');
    }

    emailChanged() {
        if (this.errors['email'].enabled) {
            this.validateEmail();
            setTimeout(() => {
                this.inputElement?.nativeElement?.childNodes[0]?.focus();
            }, 0);
        }
    }

    validateEmail() {
        // Only validates after user has interacted with the input at least once
        if (!this.model.email) {
            this.errors['email'].enabled = true;
            return true;
        }

        this.errors['email'] = {
            ...this.errors['email'],
            enabled: !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(this.model.email),
            msg: this.translateService.instant('signUpText.welcomePage.errors.invalidDomain')
        };

        if (!this.errors['email'].enabled) {
            const domain = this.model.email.split('@')[1];
            this.errors['email'].enabled = this.model.email.includes('+') && !this.featureFlagService.isDomainActivated(domain);
        }

        return this.errors['email'].enabled;
    }

    validateMobile() {
        if (!this.model.mobile) {
            this.errors['mobile'].enabled = false;
        } else if (!this.model.iso_code) {
            this.errors['mobile'].enabled = true;
        } else {
            try {
                const phoneUtil = PhoneNumberUtil.getInstance();
                const number = phoneUtil.parseAndKeepRawInput(this.model.mobile, this.model.iso_code);
                const isValid = phoneUtil.isValidNumber(number);
                this.errors['mobile'].enabled = !isValid;
            } catch (error) {
                // If parsing fails, mark as invalid
                this.errors['mobile'].enabled = true;
            }
        }
        return this.errors['mobile'].enabled;
    }

    onAreaCodeDropdownClick() {
        this._areaCodeOpening = true;
    }

    areaCodeOptionSelected(option: any) {
        this.model.iso_code = option['isoCode'] || 'AU';
        this.validateMobile();

        const fullValue = option['value'];
        const match = fullValue.match(/\(([^)]+)\)/);
        const extractedNumber = match ? match[1] : fullValue;

        this.model.area_code = extractedNumber || '+61';
        this.areaCodeDisplay = extractedNumber;

        setTimeout(() => {
            if (this.areaCodeDropdown) {
                this.areaCodeDropdown.label = extractedNumber;
            }
        }, 0);
    }

    orgSizeOptionSelected(option: any) {
        this.model.org_size = option['key'];
    }

    isContinueDisabled(): boolean {
        if (this.ff_ent_10781_sign_up) {
            return (
                this.hasAnyErrorEnabled() ||
                !this.model.email ||
                !this.model.first_name ||
                !this.model.last_name ||
                !this.model.mobile ||
                !this.model.org_name ||
                !this.model.org_size
            );
        } else {
            return this.errors['email'].enabled || !this.model.email;
        }
    }

    hasAnyErrorEnabled(): boolean {
        return Object.values(this.errors).some(
            (error) => error && typeof error === 'object' && (error as { enabled?: boolean }).enabled === true
        );
    }

    redirectToContactUsPage() {
        const data = {
            "action": Constants.SIGNUP_CLICKED_CONTACT_SUPPORT,
            "meta": {},
        };
        this.id.logButtonClick(this.token, data).subscribe();
        if (this.ff_ent_10781_sign_up) {
            window.open(Constants.SIGNUP_CONTACT_EMPLOYER_URL, '_blank');
        } else {
            window.open(Constants.SIGNUP_CONTACT_XREF_URL, '_blank');
        }
    }

    private setupAreaCodeDropdownObserver() {
        this._bodyObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of Array.from(mutation.removedNodes)) {
                    if (node instanceof HTMLElement && node.tagName === 'BS-DROPDOWN-CONTAINER') {
                        this._areaCodeOpening = false;
                    }
                }
                if (!this._areaCodeOpening) continue;
                for (const node of Array.from(mutation.addedNodes)) {
                    if (node instanceof HTMLElement && node.tagName === 'BS-DROPDOWN-CONTAINER') {
                        node.classList.add('area-code-container');
                        this._areaCodeOpening = false;
                        return;
                    }
                }
            }
        });
        this._bodyObserver.observe(document.body, { childList: true });
    }
}
